import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { FeasibilitySummary, GradivostChatResponse } from "@/api/schemas";
import type { FeasibilityExportPayload } from "@/features/feasibility/verdictSession";
import type { ParcelDocCheck, ZkFacts } from "@/features/feasibility/parcelDoc";

/**
 * G3 — sesija chat razgovora o gradivosti (zustand persist obrazac,
 * `permitpilot.*.vN` konvencija). Povratak na /provjera vraća razgovor;
 * "Nova provjera" ga čisti. Server je stateless — `lastResponse.parsed`
 * se šalje kao `previous` sa svakim novim turnom.
 *
 * Paket 2 (revizija P1-5): uz razgovor se pamti i VERDIKT — reload je
 * dosad vraćao samo korisnikov monolog, pa je arhitekt morao ponovno
 * platiti ~50 s za isti odgovor. `verdictKey` je potpis parametara na
 * kojima verdikt vrijedi; kad se parametri promijene (druga čestica,
 * druga zona) panel se označava zastarjelim umjesto da tiho laže.
 */

export interface ChatTurn {
  /** Korisnikov unos (pitanje ili odgovor na potpitanje). */
  question: string;
  /** Potpitanje koje je uslijedilo (null = provjera je pokrenuta). */
  followupQuestion: string | null;
}

/** Zadnje očitane zone čestice — pamte se odvojeno od `lastResponse` jer
 *  se auto-zona ne dohvaća ponovno kad je zona već poznata, pa su chipovi
 *  (i mogućnost klika na drugu zonu) nestajali nakon prvog odgovora
 *  (revizija P1-3). Vrijede dok se čestica ne promijeni. */
export interface ZoneMemo {
  kc: string;
  ko: string;
  zones: NonNullable<GradivostChatResponse["zone"]>["zones"];
  parcelAreaM2: number | null;
}

/** G1 — činjenice iz učitanog ZK/katastarskog izvatka + cross-check nalazi
 *  protiv G0. Žive uz sesiju (carry u projekt ih čita); „Nova provjera" ih
 *  čisti. */
export interface ParcelDocState {
  facts: ZkFacts;
  checks: ParcelDocCheck[];
}

interface GradivostChatState {
  turns: ChatTurn[];
  lastResponse: GradivostChatResponse | null;
  zoneMemo: ZoneMemo | null;
  parcelDoc: ParcelDocState | null;
  /** Zadnji verdikt motora (isti oblik koji renderira FeasibilityResult). */
  verdict: FeasibilitySummary | null;
  /** Reducirani odgovor motora za PDF izvoz (bez evidence_bundlea). */
  verdictExport: FeasibilityExportPayload | null;
  /** Potpis parametara na kojima `verdict` vrijedi (verdictSignature). */
  verdictKey: string | null;
  addTurn: (question: string, response: GradivostChatResponse) => void;
  setVerdict: (
    verdict: FeasibilitySummary,
    exportPayload: FeasibilityExportPayload,
    key: string,
  ) => void;
  setParcelDoc: (doc: ParcelDocState | null) => void;
  clear: () => void;
}

/** localStorage ključ sesije (permitpilot.*.vN konvencija ostalih storeova). */
export const GRADIVOST_CHAT_STORE_KEY = "permitpilot.gradivost.v1";

export const useGradivostChatStore = create<GradivostChatState>()(
  persist(
    (set) => ({
      turns: [],
      lastResponse: null,
      zoneMemo: null,
      parcelDoc: null,
      verdict: null,
      verdictExport: null,
      verdictKey: null,
      addTurn: (question, response) =>
        set((s) => {
          const p = response.parsed;
          const fresh = response.zone?.zones?.length ? response.zone : null;
          // Nove zone pamti; kad se čestica promijeni, staro pamćenje pada
          // (nikad zone tuđe čestice).
          const sameParcel =
            !!s.zoneMemo && s.zoneMemo.kc === (p.kc ?? "") && s.zoneMemo.ko === (p.ko ?? "");
          const zoneMemo = fresh
            ? {
                kc: p.kc ?? "",
                ko: p.ko ?? "",
                zones: fresh.zones,
                parcelAreaM2:
                  (fresh as { parcel?: { area_m2?: number } }).parcel?.area_m2 ?? null,
              }
            : sameParcel
              ? s.zoneMemo
              : null;
          return {
            turns: [...s.turns, { question, followupQuestion: response.question }],
            lastResponse: response,
            zoneMemo,
          };
        }),
      setVerdict: (verdict, exportPayload, key) =>
        set({ verdict, verdictExport: exportPayload, verdictKey: key }),
      setParcelDoc: (parcelDoc) => set({ parcelDoc }),
      clear: () =>
        set({
          turns: [],
          lastResponse: null,
          zoneMemo: null,
          parcelDoc: null,
          verdict: null,
          verdictExport: null,
          verdictKey: null,
        }),
    }),
    { name: GRADIVOST_CHAT_STORE_KEY },
  ),
);
